import { ExpressionTypes, Expression, Processor, Replacement } from "./types";

const linkExpr = /\[([\w\s-'@.0-9a-zÀ-ÿ]+)\]\(((\w+:)?[\/a-zA-Z-@.]+)\)/;

function matchExpr(expressions: Expression[], processors: Processor[]) {
  return processors.reduce(
    (acc, processor) => [
      ...acc.reduce<Expression[]>(
        (a, expr) =>
          expr.type === ExpressionTypes.TEXT
            ? [...a, ...processor.process(expr)]
            : [...a, expr],
        [],
      ),
    ],
    expressions,
  );
}

function extractResources(
  resourcesNames: string[],
  { content }: Expression,
): Expression[] {
  if (resourcesNames.length === 0) {
    return [{ type: ExpressionTypes.TEXT, content, attributes: {} }];
  }

  const match = content.match(
    new RegExp(
      `(${resourcesNames
        .map((name) => `@${name.replace(/[/\-\\^$*+?.()|[\]{}]/g, "\\$&")}`)
        .join("|")})`,
      "i",
    ),
  );

  if (!match) {
    return [{ type: ExpressionTypes.TEXT, content, attributes: {} }];
  }

  const [fullMatch, text, url] = match;
  const matchIndex = content.indexOf(fullMatch);
  const prevMatch = content.substring(0, matchIndex);
  const nextMatch = content.substring(matchIndex + fullMatch.length);

  return [
    { type: ExpressionTypes.TEXT, content: prevMatch, attributes: {} },
    { type: ExpressionTypes.RESOURCE, content: text, attributes: { url } },
    ...extractResources(resourcesNames, {
      type: ExpressionTypes.TEXT,
      content: nextMatch,
      attributes: {},
    }),
  ];
}

function replaceContent(
  content: string,
  replacements: { [key in ExpressionTypes]: Replacement },
  resourcesNames: string[],
) {
  return matchExpr(
    [
      {
        type: ExpressionTypes.TEXT,
        content: content.trim(),
        attributes: {},
      },
    ],
    [
      {
        type: ExpressionTypes.RESOURCE,
        process: (expr) => extractResources(resourcesNames, expr),
      },
    ],
  )
    .map(
      (expr) =>
        !!replacements[expr.type] &&
        replacements[expr.type](expr.content, expr.attributes),
    )
    .filter((res) => !!res);
}

function styleMentions(html: string, names: string[]): string {
  return names.reduce(
    (acc, name) =>
      name
        ? acc.replace(
            new RegExp(`@${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "g"),
            `<span style="color: #269FFF">@${name}</span>`,
          )
        : acc,
    html,
  );
}

export { replaceContent, styleMentions };
